import React, { useState, useMemo } from "react";
import {
  usePayouts,
  usePayoutStats,
  usePayoutExportHistory,
  useBulkExportPayouts,
  useBulkRejectPayouts,
  useBulkMarkPayoutsPaid,
} from "../hooks/usePayouts";
import { useLocalStorage } from "../hooks/useLocalStorage";
import SearchBar from "../components/common/SearchBar";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Pagination from "../components/common/Pagination";
import { Payout, PayoutStatus } from "../services/api";
import {
  FunnelIcon,
  BanknotesIcon,
  DocumentArrowDownIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  EyeIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

type FilterStatus = "all" | PayoutStatus;

const Payouts: React.FC = () => {
  const [selectedPayouts, setSelectedPayouts] = useState<string[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isExportHistoryModalOpen, setIsExportHistoryModalOpen] =
    useState(false);
  const [filter, setFilter] = useLocalStorage<FilterStatus>(
    "payouts-filter",
    "all",
  );
  const [searchQuery, setSearchQuery] = useLocalStorage<string>(
    "payouts-search-query",
    "",
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);

  const {
    data: payoutsResponse,
    isLoading,
  } = usePayouts({
    limit: itemsPerPage,
    page: currentPage,
    search: searchQuery || undefined,
    status: filter !== "all" ? filter : undefined,
  });

  const { data: stats } = usePayoutStats();
  const { data: exportHistoryData } = usePayoutExportHistory(10);

  const bulkExport = useBulkExportPayouts();
  const bulkReject = useBulkRejectPayouts();
  const bulkMarkPaid = useBulkMarkPayoutsPaid();

  const payouts = useMemo(
    () => payoutsResponse?.data || [],
    [payoutsResponse?.data],
  );
  const totalItems = payoutsResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const exportHistory = exportHistoryData?.data || [];

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);

  // Clear selections when data changes
  React.useEffect(() => {
    setSelectedPayouts([]);
  }, [payoutsResponse]);

  const handleSelectAll = () => {
    if (selectedPayouts.length === payouts.length) {
      setSelectedPayouts([]);
    } else {
      setSelectedPayouts(payouts.map((p) => p._id));
    }
  };

  const handleSelectPayout = (id: string) => {
    if (selectedPayouts.includes(id)) {
      setSelectedPayouts(selectedPayouts.filter((pid) => pid !== id));
    } else {
      setSelectedPayouts([...selectedPayouts, id]);
    }
  };

  const handleViewDetails = (payout: Payout) => {
    setSelectedPayout(payout);
    setIsDetailModalOpen(true);
  };

  const handleExport = () => {
    if (selectedPayouts.length === 0) return;
    bulkExport.mutate(selectedPayouts);
  };

  const handleOpenRejectModal = () => {
    if (selectedPayouts.length === 0) return;
    setRejectReason("");
    setIsRejectModalOpen(true);
  };

  const handleReject = () => {
    if (selectedPayouts.length === 0 || !rejectReason.trim()) return;
    bulkReject.mutate(
      { payoutIds: selectedPayouts, reason: rejectReason },
      { onSuccess: () => setIsRejectModalOpen(false) },
    );
  };

  const handleMarkPaid = () => {
    if (selectedPayouts.length === 0) return;
    bulkMarkPaid.mutate(selectedPayouts);
  };

  const getStatusBadge = (status: PayoutStatus) => {
    const badges: Record<
      PayoutStatus,
      { bg: string; icon: React.ReactNode; label: string }
    > = {
      open: {
        bg: "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
        icon: <ClockIcon className="h-4 w-4 mr-1" />,
        label: "Open",
      },
      in_progress: {
        bg: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
        icon: <ArrowPathIcon className="h-4 w-4 mr-1" />,
        label: "In Progress",
      },
      complete: {
        bg: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
        icon: <CheckCircleIcon className="h-4 w-4 mr-1" />,
        label: "Complete",
      },
      rejected: {
        bg: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
        icon: <XCircleIcon className="h-4 w-4 mr-1" />,
        label: "Rejected",
      },
    };
    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg}`}
      >
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if any selected payout can be exported (only open status)
  const canExport = useMemo(() => {
    return selectedPayouts.some((id) => {
      const payout = payouts.find((p) => p._id === id);
      return payout?.status === "open";
    });
  }, [selectedPayouts, payouts]);

  // Check if any selected payout can be marked as paid (only in_progress status)
  const canMarkPaid = useMemo(() => {
    return selectedPayouts.some((id) => {
      const payout = payouts.find((p) => p._id === id);
      return payout?.status === "in_progress";
    });
  }, [selectedPayouts, payouts]);

  // Check if any selected payout can be rejected (open or in_progress status)
  const canReject = useMemo(() => {
    return selectedPayouts.some((id) => {
      const payout = payouts.find((p) => p._id === id);
      return payout?.status === "open" || payout?.status === "in_progress";
    });
  }, [selectedPayouts, payouts]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Payouts
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage customer payout requests
          </p>
        </div>
        <button
          onClick={() => setIsExportHistoryModalOpen(true)}
          className="mt-2 sm:mt-0 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
          Export History
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">Open</div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.open || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              In Progress
            </div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {stats.inProgress || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Complete
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.complete || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Amount
            </div>
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {formatCurrency(stats.totalEuroAmount || 0)}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search by dealer name or ID..."
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterStatus)}
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="complete">Complete</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedPayouts.length > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedPayouts.length} payout(s) selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleExport}
                disabled={!canExport || bulkExport.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                {bulkExport.isPending ? "Exporting..." : "Export"}
              </button>
              <button
                onClick={handleMarkPaid}
                disabled={!canMarkPaid || bulkMarkPaid.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                {bulkMarkPaid.isPending ? "Processing..." : "Mark as Paid"}
              </button>
              <button
                onClick={handleOpenRejectModal}
                disabled={!canReject || bulkReject.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircleIcon className="h-4 w-4 mr-1" />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payouts Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : payouts.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={
                        selectedPayouts.length === payouts.length &&
                        payouts.length > 0
                      }
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Payout ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Bank Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {payouts.map((payout) => (
                  <tr
                    key={payout._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedPayouts.includes(payout._id)}
                        onChange={() => handleSelectPayout(payout._id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900 dark:text-white">
                        {payout.payoutId}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {payout.customerSnapshot.firstName}{" "}
                          {payout.customerSnapshot.lastName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {payout.customerSnapshot.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(
                            payout.amount || payout.euroAmount || 0,
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {payout.pointsAmount.toLocaleString()} EP
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {payout.bankDetails?.bankName ||
                            payout.bankName ||
                            "-"}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {(payout.bankDetails?.iban || payout.iban)?.substring(
                            0,
                            8,
                          )}
                          ...
                          {(payout.bankDetails?.iban || payout.iban)?.slice(-4)}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(payout.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(payout.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetails(payout)}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <BanknotesIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No payouts found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || filter !== "all"
              ? "Try adjusting your search or filters"
              : "No payout requests yet"}
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedPayout && isDetailModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsDetailModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BanknotesIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Payout Details
                    </h3>
                    <p className="text-sm text-primary-100">
                      {selectedPayout.payoutId}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsDetailModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Status
                    </span>
                    {getStatusBadge(selectedPayout.status)}
                  </div>

                  {/* Amount */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Amount
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Euro Amount
                        </label>
                        <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                          {formatCurrency(
                            selectedPayout.amount ||
                              selectedPayout.euroAmount ||
                              0,
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Points
                        </label>
                        <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                          {selectedPayout.pointsAmount.toLocaleString()} EP
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Customer */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Customer
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Name
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedPayout.customerSnapshot.firstName}{" "}
                          {selectedPayout.customerSnapshot.lastName}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Email
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white break-all">
                          {selectedPayout.customerSnapshot.email}
                        </p>
                      </div>
                      {selectedPayout.customerSnapshot.street && (
                        <div className="col-span-2">
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Address
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedPayout.customerSnapshot.street}{" "}
                            {selectedPayout.customerSnapshot.houseNumber}
                            {selectedPayout.customerSnapshot.addressAddition &&
                              `, ${selectedPayout.customerSnapshot.addressAddition}`}
                            <br />
                            {selectedPayout.customerSnapshot.zipCode}{" "}
                            {selectedPayout.customerSnapshot.city}
                            {selectedPayout.customerSnapshot.country &&
                              `, ${selectedPayout.customerSnapshot.country}`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Bank Details
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Account Holder
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedPayout.bankDetails?.accountHolder || "-"}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Bank Name
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedPayout.bankDetails?.bankName ||
                            selectedPayout.bankName ||
                            "-"}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          IBAN
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                          {selectedPayout.bankDetails?.iban ||
                            selectedPayout.iban ||
                            "-"}
                        </p>
                      </div>
                      {(selectedPayout.bankDetails?.bic ||
                        selectedPayout.bic) && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            BIC
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedPayout.bankDetails?.bic ||
                              selectedPayout.bic}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status History */}
                  {selectedPayout.statusHistory &&
                    selectedPayout.statusHistory.length > 0 && (
                      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          Status History
                        </h4>
                        <div className="space-y-2">
                          {selectedPayout.statusHistory.map(
                            (history, index) => (
                              <div
                                key={index}
                                className="flex items-start space-x-3"
                              >
                                <div className="flex-shrink-0">
                                  {getStatusBadge(history.status)}
                                </div>
                                <div>
                                  <p className="text-sm text-gray-900 dark:text-white">
                                    {formatDate(history.changedAt.toString())}
                                  </p>
                                  {history.reason && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {history.reason}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                  {/* Rejection Reason */}
                  {selectedPayout.rejectionReason && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-red-800 dark:text-red-200">
                          Rejection Reason
                        </span>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {selectedPayout.rejectionReason}
                      </p>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Timestamps
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Created
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedPayout.createdAt)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Updated
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedPayout.updatedAt)}
                        </p>
                      </div>
                      {selectedPayout.exportedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Exported
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(selectedPayout.exportedAt.toString())}
                          </p>
                        </div>
                      )}
                      {selectedPayout.processedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Processed
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(selectedPayout.processedAt.toString())}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsRejectModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-red-600 px-6 py-4">
                <h3 className="text-xl font-semibold text-white">
                  Reject Payouts
                </h3>
                <p className="text-sm text-red-100">
                  {selectedPayouts.length} payout(s) will be rejected
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter the reason for rejection..."
                />
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setIsRejectModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || bulkReject.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkReject.isPending ? "Rejecting..." : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export History Modal */}
      {isExportHistoryModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsExportHistoryModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">
                  Export History
                </h3>
                <button
                  onClick={() => setIsExportHistoryModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-96 overflow-y-auto">
                {exportHistory.length > 0 ? (
                  <div className="space-y-3">
                    {exportHistory.map((item) => (
                      <div
                        key={item.batchId}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div>
                          <p className="text-sm font-mono text-gray-900 dark:text-white">
                            {item.batchId}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(item.exportedAt)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.count} payouts
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatCurrency(item.totalAmount)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400">
                    No exports yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payouts;
